import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, CalendarDays, ChevronRight, Inbox, Loader2, Radio } from 'lucide-react';
import { sessionHubService, HubSession, HubSessionStage, HubWelcome } from '../../services/sessionHub.service';
import { SharedItems } from './SharedItems';
import { WelcomePlayer } from './WelcomePlayer';
import { SessionBefore } from './SessionBefore';

const STAGE: Record<HubSessionStage, { label: string; cls: string }> = {
  before: { label: 'Coming up', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  live: { label: 'Live now', cls: 'bg-green-50 text-green-600 border-green-200' },
  after: { label: 'Finished', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

const MODE: Record<'in_person' | 'remote_teams', string> = { in_person: 'Attending in person', remote_teams: 'Joining on Teams' };

function Placeholder({ icon: Icon, title, text }: { icon: React.ElementType; title: string; text: string }) {
  return (
    <div className="rounded-xl bg-white p-10 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
      <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4"><Icon size={24} className="text-slate-400" /></div>
      <p className="text-slate-600 text-sm font-semibold">{title}</p>
      <p className="text-slate-400 text-xs mt-2">{text}</p>
    </div>
  );
}

/** The session's own welcome, if it has one; nothing otherwise. */
function SessionWelcome({ link, sessionId }: { link: string; sessionId: string }) {
  const [welcome, setWelcome] = useState<HubWelcome | null>(null);
  const load = useCallback(() => {
    sessionHubService.sessionWelcome(link, sessionId).then(setWelcome).catch(() => setWelcome(null));
  }, [link, sessionId]);
  useEffect(() => { load(); }, [load]);
  if (!welcome || (!welcome.message && !welcome.video)) return null;
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      {welcome.video && <div className="p-3 sm:p-4"><WelcomePlayer url={welcome.video.url} captions={welcome.captions} onExpired={load} /></div>}
      {welcome.message && <p className="px-5 pb-5 pt-3 text-sm text-slate-600 whitespace-pre-line leading-relaxed">{welcome.message}</p>}
    </div>
  );
}

function SessionDetail({ link, session, onBack }: { link: string; session: HubSession; onBack: () => void }) {
  return (
    <div className="flex flex-col gap-5">
      <button type="button" onClick={onBack} className="self-start flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
        <ArrowLeft size={16} /> All sessions
      </button>
      <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-bold text-navy">{session.name}</p>
          {session.mode && <p className="text-xs text-slate-400 mt-1">{MODE[session.mode]}</p>}
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STAGE[session.stage].cls}`}>{STAGE[session.stage].label}</span>
      </div>
      <SessionWelcome link={link} sessionId={session.sessionId} />
      {session.stage === 'before' && <SessionBefore link={link} sessionId={session.sessionId} />}
      {session.stage === 'live' && (
        <Placeholder icon={Radio} title="This session is live" text="Ways to take part from your phone will appear here." />
      )}
      {(session.stage === 'after' || session.sharedItems > 0) && (
        <div className="flex flex-col gap-3">
          <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Shared from this session</p>
          <SharedItems link={link} sessionId={session.sessionId} />
        </div>
      )}
    </div>
  );
}

export function HubSessions({ link }: { link: string }) {
  const [sessions, setSessions] = useState<HubSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    sessionHubService.sessions(link).then((r) => setSessions(r.sessions)).catch((e) => setError(e.message));
  }, [link]);

  if (error) return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>;
  if (!sessions) return <div className="flex justify-center items-center py-20 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>;

  const open = sessions.find((s) => s.sessionId === openId);
  if (open) return <SessionDetail link={link} session={open} onBack={() => setOpenId(null)} />;

  if (sessions.length === 0) {
    return <Placeholder icon={CalendarDays} title="No sessions yet" text="Sessions you are invited to will appear here." />;
  }

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <ul className="divide-y divide-[#e2e8f0]">
        {sessions.map((s) => (
          <li key={s.sessionId}>
            <button type="button" onClick={() => setOpenId(s.sessionId)} className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-slate-50 transition-colors">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><CalendarDays size={18} /></div>
              <div className="flex-1 min-w-[12rem]">
                <p className="text-sm font-semibold text-navy">{s.name}</p>
                <p className="text-xs text-slate-400 flex items-center gap-1.5">
                  {s.mode ? MODE[s.mode] : 'Shared with you'}
                  {s.sharedItems > 0 && <><span>·</span><Inbox size={12} /> {s.sharedItems} shared</>}
                </p>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STAGE[s.stage].cls}`}>{STAGE[s.stage].label}</span>
              <ChevronRight size={16} className="text-slate-300" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
